import Vue from 'vue'
import Router from 'vue-router'
import i18n from '@/config/i18n'
import {guardLogin} from '@/guards/guards'

// Containers
const DefaultContainer = () => import('@/components/DefaultContainer');

// Views
const Dashboard = () => import('@/views/Dashboard');

// Models
const ModelList = () => import('@/views/models/ModelList');
const ModelShow = () => import('@/views/models/ModelShow');
const ModelEdit = () => import('@/views/models/ModelEdit');
const ModelNew = () => import('@/views/models/ModelNew');
const ModelClone = () => import('@/views/models/ModelClone');
const ModelSimulation = () => import('@/views/models/ModelSimulation');

// Category
const CategoryList = () => import('@/views/category/CategoryList');
const CategoryNew = () => import('@/views/category/CategoryNew');
const CategoryShow = () => import('@/views/category/CategoryShow');
const CategoryEdit = () => import('@/views/category/CategoryEdit');

// Company
const CompanyList = () => import('@/views/company/CompanyList');
const CompanyShow = () => import('@/views/company/CompanyShow');
const CompanyNew = () => import('@/views/company/CompanyNew');
const CompanyEdit = () => import('@/views/company/CompanyEdit');

// Profiles
const ProfileList = () => import('@/views/profile/ProfileList');
const ProfileShow = () => import('@/views/profile/ProfileShow');
const ProfileNew = () => import('@/views/profile/ProfileNew');
const ProfileEdit = () => import('@/views/profile/ProfileEdit');

// Jaspers
const JasperList = () => import('@/views/jasper/JasperList');
const JasperShow = () => import('@/views/jasper/JasperShow');
const JasperEdit = () => import('@/views/jasper/JasperEdit');
const JasperNew = () => import('@/views/jasper/JasperNew');

// DocumentType
const DocumentTypeList = () => import('@/views/doctype/DocTypeList');
const DocumentTypeShow = () => import('@/views/doctype/DocTypeShow');
const DocumentTypeEdit = () => import('@/views/doctype/DocTypeEdit');
const DocumentTypeNew = () => import('@/views/doctype/DocTypeNew');

// CodingSchema
const CodingSchemaList = () => import('@/views/codingschema/CodingSchemaList');
const CodingSchemaShow = () => import('@/views/codingschema/CodingSchemaShow');
const CodingSchemaEdit = () => import('@/views/codingschema/CodingSchemaEdit');
const CodingSchemaNew = () => import('@/views/codingschema/CodingSchemaNew');

// ProcedureAndPatientDetails
const ProcedureAndPatientDetailsList = () => import('@/views/patientAndProcedureDetails/ProcedureDetailsList');
const ProcedureAndPatientDetailsNew = () => import('@/views/patientAndProcedureDetails/ProcedureDetailsNew');
const ProcedureAndPatientDetailsShow = () => import('@/views/patientAndProcedureDetails/ProcedureDetailsShow');
const ProcedureAndPatientDetailsEdit = () => import('@/views/patientAndProcedureDetails/ProcedureDetailsEdit');

// PatientConstraint
const PatientConstraintList = () => import('@/views/patientConstraints/ConstraintList');
const PatientConstraintShow = () => import('@/views/patientConstraints/ConstraintShow');
const PatientConstraintEdit = () => import('@/views/patientConstraints/ConstraintEdit');
const PatientConstraintNew = () => import('@/views/patientConstraints/ConstraintNew');

// UploadImage
const UploadImageList = () => import('@/views/uploadimage/UploadImageList');
const UploadImageNew = () => import('@/views/uploadimage/UploadImageNew');

// Sources
const SourceList = () => import('@/views/sources/SourceList');
const SourceNew = () => import('@/views/sources/SourceNew');
const SourceEdit = () => import('@/views/sources/SourceEdit');
const SourceShow = () => import('@/views/sources/SourceShow');

// Procedure Type
const ProcedureTypeList = () => import('@/views/procedureTypes/ProcedureTypeList');
const ProcedureTypeNew = () => import('@/views/procedureTypes/ProcedureTypeNew');
const ProcedureTypeShow = () => import('@/views/procedureTypes/ProcedureTypeShow');
const ProcedureTypeEdit = () => import('@/views/procedureTypes/ProcedureTypeEdit');

// Procedures
const ProcedureList = () => import('@/views/procedure/ProcedureList');
const ProcedureNew = () => import('@/views/procedure/ProcedureNew');
const ProcedureCsv = () => import('@/views/procedure/ProcedureCsv');
const ProcedureShow = () => import('@/views/procedure/ProcedureShow');
const ProcedureEdit = () => import('@/views/procedure/ProcedureEdit');

// ProcedureModels
const ProcedureModelList = () => import('@/views/procedureModels/ProcedureModelList');
const ProcedureModelShow = () => import('@/views/procedureModels/ProcedureModelShow');
const ProcedureModelEdit = () => import('@/views/procedureModels/ProcedureModelEdit');
const ProcedureModelNew = () => import('@/views/procedureModels/ProcedureModelNew');

// PendingConsents
const PendingConsentList = () => import('@/views/pendingConsents/PendingConsentList');
const PendingConsentShow = () => import('@/views/pendingConsents/PendingConsentShow');

// InformedConsents
const InformedConsentList = () => import('@/views/informedConsents/InformedConsentList');
const InformedConsentShow = () => import('@/views/informedConsents/InformedConsentShow');
const InformedConsentEdit = () => import('@/views/informedConsents/InformedConsentEdit');
const InformedConsentNew = () => import('@/views/informedConsents/InformedConsentNew');

// Error pages
const Page404 = () => import('@/views/404');

const Login = () => import('@/views/Login');
const UserProfile = () => import('@/views/users/UserProfile');

Vue.use(Router);
Vue.use(i18n);


let router = new Router({
  mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  scrollBehavior: () => ({y: 0}),
  routes: [
    {
      path: '/login',
      name: 'loginRoute',
      meta: {title: 'Login'},
      component: Login
    },
    {
      path: '/',
      meta: {label: ''},
      redirect: {name: 'postAuthRoute'},
      component: DefaultContainer,
      children: [
        {
          path: 'postAuth',
          name: 'postAuthRoute',
          beforeEnter: guardLogin,
        },
        {
          path: 'dashboard',
          name: 'dashboardRoute',
          meta: {label: i18n.t('title.dashboard')},
          component: Dashboard
        },
        {
          path: 'models',
          redirect: '/models/list',
          name: 'modelRoute',
          meta: {label: i18n.tc('title.models', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'modelListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.models', 0).toLocaleLowerCase()})},
              component: ModelList
            },
            {
              path: 'show/:id',
              props: true,
              name: 'modelShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.models', 1).toLocaleLowerCase()})},
              component: ModelShow
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'modelEditRoute',
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.models', 1).toLocaleLowerCase()})},
              component: ModelEdit
            },
            {
              path: 'simulation/:code',
              props: true,
              name: 'modelSimulateRequestRoute',
              meta: {label: i18n.t('page.title.simulation')},
              component: ModelSimulation
            },
            {
              path: 'new',
              name: 'modelNewRoute',
              meta: {label: i18n.tc('page.title.create', 1, {n: i18n.tc('title.models', 1).toLocaleLowerCase()})},
              component: ModelNew
            },
            {
              path: 'clone/:id',
              props: true,
              name: 'modelCloneRoute',
              meta: {label: i18n.tc('page.title.duplicate', 0, {n: i18n.tc('title.models', 1).toLocaleLowerCase()})},
              component: ModelClone
            }
          ]
        },
        {
          path: 'category',
          redirect: {name: 'categoryListRoute'},
          name: 'categoryRoute',
          meta: {label: i18n.tc('title.categories', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'categoryListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.categories', 0).toLocaleLowerCase()})},
              component: CategoryList
            },
            {
              path: 'new',
              name: 'categoryNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.categories', 1).toLocaleLowerCase()})},
              component: CategoryNew
            },
            {
              path: 'show/:id',
              props: true,
              name: 'categoryShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.categories', 1).toLocaleLowerCase()})},
              component: CategoryShow
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'categoryEditRoute',
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.categories', 1).toLocaleLowerCase()})},
              component: CategoryEdit
            }
          ]
        },
        {
          path: 'patientAndProcedureDetails',
          redirect: '/patientAndProcedureDetails/list',
          name: 'patientAndProcedureDetailsRoute',
          meta: {label: i18n.tc('title.ppd', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'patientAndProcedureDetailsListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.ppd', 0).toLocaleLowerCase()})},
              component: ProcedureAndPatientDetailsList
            },
            {
              path: 'show/:id',
              props: true,
              name: 'patientAndProcedureDetailsShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.ppd', 0).toLocaleLowerCase()})},
              component: ProcedureAndPatientDetailsShow
            },
            {
              path: 'new',
              props: true,
              name: 'patientAndProcedureDetailsNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.ppd', 0).toLocaleLowerCase()})},
              component: ProcedureAndPatientDetailsNew
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'patientAndProcedureDetailsEditRoute',
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.ppd', 0).toLocaleLowerCase()})},
              component: ProcedureAndPatientDetailsEdit
            }
          ]
        }, {
          path: 'patientConstraint',
          redirect: {name: 'patientConstraintListRoute'},
          name: 'patientConstraintRoute',
          meta: {label: i18n.tc('title.patientConstraint', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'patientConstraintListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.patientConstraint', 0).toLocaleLowerCase()})},
              component: PatientConstraintList
            },
            {
              path: 'show/:id',
              props: true,
              name: 'patientConstraintShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.patientConstraint', 0).toLocaleLowerCase()})},
              component: PatientConstraintShow
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'patientConstraintEditRoute',
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.patientConstraint', 0).toLocaleLowerCase()})},
              component: PatientConstraintEdit
            },
            {
              path: 'new',
              name: 'patientConstraintNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.patientConstraint', 0).toLocaleLowerCase()})},
              component: PatientConstraintNew
            }
          ]
        },
        {
          path: 'users',
          redirect: '/users/profile',
          name: 'usersRoute',
          meta: {label: i18n.tc('title.users', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'profile',
              name: 'usersProfileRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.users', 0).toLocaleLowerCase()})},
              component: UserProfile,
              props: true
            }
          ]
        },
        {
          path: 'doctype',
          redirect: {name: 'doctypeListRoute'},
          name: 'doctypeRoute',
          meta: {label: i18n.t('title.documentType')},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'doctypeListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.documentType', 0).toLocaleLowerCase()})},
              component: DocumentTypeList
            },
            {
              path: 'show/:id',
              name: 'doctypeShowRoute',
              props: true,
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.documentType', 0).toLocaleLowerCase()})},
              component: DocumentTypeShow
            },
            {
              path: 'edit/:id',
              name: 'doctypeEditRoute',
              props: true,
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.documentType', 0).toLocaleLowerCase()})},
              component: DocumentTypeEdit
            },
            {
              path: 'new',
              name: 'doctypeNewRoute',
              props: true,
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.documentType', 0).toLocaleLowerCase()})},
              component: DocumentTypeNew
            }
          ]
        },
        {
          path: 'documentTypeCodingSchema',
          redirect: {name: 'documentTypeCodingSchemaListRoute'},
          name: 'documentTypeCodingSchemaRoute',
          meta: {label: i18n.t('title.codingSchema')},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'documentTypeCodingSchemaListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.t('title.codingSchema').toLocaleLowerCase()})},
              component: CodingSchemaList
            },
            {
              path: 'show/:id',
              name: 'documentTypeCodingSchemaShowRoute',
              props: true,
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.codingSchema', 0).toLocaleLowerCase()})},
              component: CodingSchemaShow
            },
            {
              path: 'edit/:id',
              name: 'documentTypeCodingSchemaEditRoute',
              props: true,
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.codingSchema', 0).toLocaleLowerCase()})},
              component: CodingSchemaEdit
            },
            {
              path: 'new',
              name: 'documentTypeCodingSchemaNewRoute',
              props: true,
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.codingSchema', 0).toLocaleLowerCase()})},
              component: CodingSchemaNew
            }
          ]
        },
        {
          path: 'company',
          redirect: '/company/list',
          name: 'companyRoute',
          meta: {label: i18n.tc('title.companies', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'companyListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.companies', 0).toLocaleLowerCase()})},
              component: CompanyList
            },
            {
              path: 'show/:id',
              props: true,
              name: 'companyShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.companies', 1).toLocaleLowerCase()})},
              component: CompanyShow
            },
            {
              path: 'new',
              name: 'companyNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.companies', 1).toLocaleLowerCase()})},
              component: CompanyNew
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'companyEditRoute',
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.companies', 1).toLocaleLowerCase()})},
              component: CompanyEdit
            }
          ]
        },
        {
          path: 'profiles',
          redirect: {name: 'profilesListRoute'},
          name: 'profilesRoute',
          meta: {label: i18n.tc('title.profiles', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'profilesListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.profiles', 0).toLocaleLowerCase()})},
              component: ProfileList
            },
            {
              path: 'show/:id',
              props: true,
              name: 'profilesShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.profiles', 1).toLocaleLowerCase()})},
              component: ProfileShow
            },
            {
              path: 'new',
              props: true,
              name: 'profilesNewRoute',
              meta: {label: i18n.tc('page.title.create', 1, {n: i18n.tc('title.profiles', 1).toLocaleLowerCase()})},
              component: ProfileNew
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'profilesEditRoute',
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.profiles', 1).toLocaleLowerCase()})},
              component: ProfileEdit
            }
          ]
        },
        {
          path: 'jasper',
          redirect: '/jasper/list',
          name: i18n.tc('title.jaspers', 0),
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'jasperListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.jaspers', 0).toLocaleLowerCase()})},
              component: JasperList
            },
            {
              path: 'show/:uri',
              props: true,
              name: 'jasperShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.jaspers', 0).toLocaleLowerCase()})},
              component: JasperShow
            },
            {
              path: 'new',
              props: true,
              name: 'jasperNewRoute',
              meta: {label: i18n.tc('page.title.create', 1, {n: i18n.tc('title.jaspers', 0).toLocaleLowerCase()})},
              component: JasperNew
            },
            {
              path: 'edit/:uri',
              props: true,
              name: 'jasperEditRoute',
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.jaspers', 0).toLocaleLowerCase()})},
              component: JasperEdit
            }
          ]
        },
        {
          path: 'uploadimage',
          redirect: '/uploadimage/list',
          name: i18n.tc('title.uploadimage', 0),
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'uploadImageListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.uploadimage', 1).toLocaleLowerCase()})},
              component: UploadImageList
            },
            {
              path: 'new',
              props: true,
              name: 'uploadImageNewRoute',
              meta: {label: i18n.tc('page.title.upload', 0, {n: i18n.tc('title.uploadimage', 0).toLocaleLowerCase()})},
              component: UploadImageNew
            }
          ]
        },
        {
          path: 'sources',
          redirect: '/sources/list',
          name: 'sourceRoute',
          meta: {label: i18n.tc('title.sources', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'sourceListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.sources', 1).toLocaleLowerCase()})},
              component: SourceList
            },
            {
              path: 'new',
              props: true,
              name: 'sourceNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.sources', 0).toLocaleLowerCase()})},
              component: SourceNew
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'sourceEditRoute',
              meta: {label: i18n.tc('page.title.edit', 1, {n: i18n.tc('title.sources', 0).toLocaleLowerCase()})},
              component: SourceEdit
            },
            {
              path: 'show/:id',
              props: true,
              name: 'sourceShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.sources', 0).toLocaleLowerCase()})},
              component: SourceShow
            },
          ]
        },
        {
          path: 'procedureTypes',
          redirect: '/procedureTypes/list',
          name: 'procedureTypeRoute',
          meta: {label: i18n.tc('title.procedureTypes', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'procedureTypeListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.procedureTypes', 1).toLocaleLowerCase()})},
              component: ProcedureTypeList
            },
            {
              path: 'new',
              props: true,
              name: 'procedureTypeNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.procedureTypes', 0).toLocaleLowerCase()})},
              component: ProcedureTypeNew
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'procedureTypeEditRoute',
              meta: {label: i18n.tc('page.title.edit', 1, {n: i18n.tc('title.procedureTypes', 0).toLocaleLowerCase()})},
              component: ProcedureTypeEdit
            },
            {
              path: 'show/:id',
              props: true,
              name: 'procedureTypeShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.procedureTypes', 0).toLocaleLowerCase()})},
              component: ProcedureTypeShow
            },
          ]
        },
        {
          path: 'procedures',
          redirect: '/procedures/list',
          name: 'procedureRoute',
          meta: {label: i18n.tc('title.procedures', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'procedureListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.procedures', 1).toLocaleLowerCase()})},
              component: ProcedureList
            },
            {
              path: 'new',
              props: true,
              name: 'procedureNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.procedures', 0).toLocaleLowerCase()})},
              component: ProcedureNew
            },
            {
              path: 'csv',
              props: true,
              name: 'procedureCsvRoute',
              meta: {label: i18n.tc('page.title.upload', 2, {n: i18n.tc('title.procedures', 0).toLocaleLowerCase()})},
              component: ProcedureCsv
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'procedureEditRoute',
              meta: {label: i18n.tc('page.title.edit', 1, {n: i18n.tc('title.procedures', 0).toLocaleLowerCase()})},
              component: ProcedureEdit
            },
            {
              path: 'show/:id',
              props: true,
              name: 'procedureShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.procedures', 0).toLocaleLowerCase()})},
              component: ProcedureShow
            },
          ]
        },
        {
          path: 'procedureModels',
          redirect: '/procedureModels/list',
          name: 'procedureModelRoute',
          meta: {label: i18n.tc('title.procedureModels', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'procedureModelListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.procedureModels', 1).toLocaleLowerCase()})},
              component: ProcedureModelList
            },
            {
              path: 'new',
              props: true,
              name: 'procedureModelNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.procedureModels', 0).toLocaleLowerCase()})},
              component: ProcedureModelNew
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'procedureModelEditRoute',
              meta: {label: i18n.tc('page.title.edit', 1, {n: i18n.tc('title.procedureModels', 0).toLocaleLowerCase()})},
              component: ProcedureModelEdit
            },
            {
              path: 'show/:id',
              props: true,
              name: 'procedureModelShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.procedureModels', 0).toLocaleLowerCase()})},
              component: ProcedureModelShow
            },
          ]
        },
        {
          path: 'pendingConsents',
          redirect: '/pendingConsents/list',
          name: 'pendingConsentRoute',
          meta: {label: i18n.tc('title.pendingConsents', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'pendingConsentListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.pendingConsents', 1).toLocaleLowerCase()})},
              component: PendingConsentList
            },
            {
              path: 'show/:id',
              props: true,
              name: 'pendingConsentShowRoute',
              meta: {label: i18n.tc('page.title.detail', 0, {n: i18n.tc('title.pendingConsents', 0).toLocaleLowerCase()})},
              component: PendingConsentShow
            },
          ]
        },
        {
          path: 'informationConsents',
          redirect: '/informationConsents/list',
          name: 'informationConsentsRoute',
          meta: {label: i18n.tc('title.informationConsents', 0)},
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: 'list',
              name: 'informationConsentsListRoute',
              meta: {label: i18n.tc('page.title.list', 0, {n: i18n.tc('title.informationConsents', 1).toLocaleLowerCase()})},
              component: InformedConsentList
            },
            {
              path: 'show/:id',
              props: true,
              name: 'informationConsentsShowRoute',
              meta: {label: i18n.tc('page.title.show', 0, {n: i18n.tc('title.informationConsents', 0).toLocaleLowerCase()})},
              component: InformedConsentShow
            },
            {
              path: 'edit/:id',
              props: true,
              name: 'informationConsentsEditRoute',
              meta: {label: i18n.tc('page.title.edit', 0, {n: i18n.tc('title.informationConsents', 0).toLocaleLowerCase()})},
              component: InformedConsentEdit
            },{
              path: 'new',
              props: true,
              name: 'informationConsentsNewRoute',
              meta: {label: i18n.tc('page.title.create', 0, {n: i18n.tc('title.informationConsents', 0).toLocaleLowerCase()})},
              component: InformedConsentNew
            },
          ]
        }
      ]
    },
    {
      path: "*",
      name: i18n.t('title.errors'),
      component: Page404,
      props: true
    }
  ]
});

export default router
